import { useEffect, useRef, useState } from 'react';
import { cn } from '../../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText } from '../../../../../styling/stylingHelper';
import {
  getBackprocessPlatforms,
  sendIssueToCodeMonkey,
  setAICheckerManualIssue,
  deleteAICheckerOrder,
  deleteAICheckerRevenue,
  updateAICheckerSafeSkip,
  type AICheckerTrace,
} from '../../../../../services/admin/adminService';
import {
  AlertTriangle,
  Bug,
  Eye,
  Layers,
  ListChecks,
  MoreVertical,
  Plus,
  RotateCw,
  ShieldCheck,
  Terminal,
  Trash2,
} from 'lucide-react';
import { AddRestaurantPlatformModal } from '../../AddRestaurantPlatformModal';
import { buildAICheckerEmailPrompt } from './emailPrompt';
import { deriveKnownCaseSender, extractSenderAddress } from './senderUtils';
import type { RowRunKind } from './rowRuns';
import type { TFn } from '../types';

// Per-row "⋮" overflow menu — collects the less-common row actions (code-monkey handoff + mark-safe)
// into one kebab so the action cell stays compact. The dropdown is positioned with fixed coords captured
// from the button rect so it is not clipped by the table's horizontal-scroll container. Marking safe adds
// the sender/subject to the user safe-skip lists and immediately re-audits this email.
// Backprocessing runs inline from the row "…" menu (supplier dropdown + run button). The platform list
// and the last supplier the super-admin picked live at module scope so they survive menu/row unmounts —
// processing several emails against the same supplier in a row needs no re-selecting.
let backprocessPlatformsCache: string[] | null = null;
let lastBackprocessPlatform = '';

export function RowActionsMenu({ trace, onChanged, onOpenDetail, onRunAction, isRunning, t }: { trace: AICheckerTrace; onChanged?: () => void; onOpenDetail?: () => void; onRunAction?: (kind: RowRunKind, platform?: string) => void; isRunning?: (kind: RowRunKind) => boolean; t: TFn }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'menu' | 'sender' | 'subject'>('menu');
  const [monkeyFlow, setMonkeyFlow] = useState<'wrong-processing' | 'email-issue'>('wrong-processing');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  // Viewport space available on the chosen side — the panel caps to it and scrolls internally,
  // so no menu option can ever be cropped off-screen.
  const [maxH, setMaxH] = useState<number | null>(null);
  const [bpPlatforms, setBpPlatforms] = useState<string[]>(backprocessPlatformsCache || []);
  const [bpPlatform, setBpPlatform] = useState('');
  const [addPlatformOpen, setAddPlatformOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Re-audit just needs the rfc822 id to re-fetch the email — available for any email type
  // (order / revenue / unprocessed), regardless of work state.
  const canReaudit = !!trace.rfc822MessageId;
  // Backprocessing re-fetches the email by its Gmail id to read every PDF attachment.
  const canBackprocess = !!trace.gmailMessageId;
  // The "all files" deep check is the counterpart to a manual backprocess: the email carries the
  // supplier's own subject, so the plain deep check would sniff it and only verify one PDF. Emails
  // whose subject already announces backprocessing don't need it — the plain one covers them.
  const canDeepCheckAllFiles = !!trace.rfc822MessageId && !/backprocessing\s*#/i.test(trace.emailSubject || '');
  // Deleting the linked order/revenue stays available from the menu even after the email is marked good
  // (the inline delete button only shows on flagged rows). Backprocessed rows can have a stale audit.
  const isRevenue = trace.matchedIn === 'revenue';
  const canDelete = (trace.hasStoredOrderData || trace.matchedIn === 'order' || isRevenue) && !!trace.gmailMessageId;
  // Reprocess with the message_id dedup skipped: the orderhistory row exists (which is exactly why a
  // plain reprocess would no-op) but the factuur was never stored. Replay-safe server-side.
  const canAddMissingFactuur =
    !!trace.rfc822MessageId && (trace.account === 'cloud63' || trace.account === 'cloud64')
    && trace.factuurExpected === true && !trace.factuurCreated;

  // Compares the stored rows against the email's files, so it needs an order/factuur to compare.
  const canCheckWrongProcessing = !!trace.gmailMessageId && !isRevenue && (trace.hasStoredOrderData || trace.matchedIn === 'order');
  const selectedMonkeyFlow = canCheckWrongProcessing ? monkeyFlow : 'email-issue';

  const close = () => { setOpen(false); setView('menu'); setError(null); setDone(null); setBusy(false); };

  // Every action that fetches the email and/or calls AI runs ON THE ROW, not in here: the menu closes
  // immediately and the row shows a spinner then the outcome — so a whole screenful of rows can be
  // checked one click after another instead of waiting out each run with the menu pinned open.
  const startRun = (kind: RowRunKind, platform?: string) => {
    if (isRunning?.(kind)) return;
    onRunAction?.(kind, platform);
    close();
  };

  const toggle = () => {
    if (open) { close(); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const right = window.innerWidth - r.right;
      // Flip above the button when there isn't enough room below for the menu but there is above.
      const ESTIMATED_H = 380;
      const spaceBelow = window.innerHeight - r.bottom - 8;
      const spaceAbove = r.top - 8;
      const flipUp = spaceBelow < ESTIMATED_H && spaceAbove > spaceBelow;
      // Cap the panel to the space on the chosen side — a menu taller than that scrolls inside.
      setMaxH(Math.max(flipUp ? spaceAbove : spaceBelow, 120));
      setPos(flipUp ? { bottom: window.innerHeight - r.top + 4, right } : { top: r.bottom + 4, right });
    }
    setOpen(true);
  };

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  // Load the backprocess supplier list when the menu opens (module-cached — one fetch per page load).
  // Pre-select the last supplier used this session, else the platform the audit already resolved.
  useEffect(() => {
    if (!open || !canBackprocess) return;
    const init = (list: string[]) => {
      setBpPlatforms(list);
      if (lastBackprocessPlatform && list.includes(lastBackprocessPlatform)) setBpPlatform(lastBackprocessPlatform);
      else if (trace.platform && list.includes(trace.platform)) setBpPlatform(trace.platform);
      else if (list.length === 1) setBpPlatform(list[0]);
    };
    if (backprocessPlatformsCache) { init(backprocessPlatformsCache); return; }
    getBackprocessPlatforms()
      .then((list) => { backprocessPlatformsCache = list; init(list); })
      .catch((err) => setError(err.message || 'Failed'));
  }, [open, canBackprocess, trace.platform]);

  const openSafe = (type: 'sender' | 'subject') => {
    setView(type);
    setError(null);
    setValue(type === 'sender' ? deriveKnownCaseSender(trace.emailFrom) : (trace.emailSubject || ''));
  };

  const handleCodeMonkey = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await sendIssueToCodeMonkey({
        flow: 'email-issue',
        caseInfo: buildAICheckerEmailPrompt(trace),
        context: {
          id: trace.id,
          account: trace.account,
          rfc822MessageId: trace.rfc822MessageId,
          gmailMessageId: trace.gmailMessageId,
          platform: trace.platform,
          subject: trace.emailSubject,
        },
      });
      setDone(t('adminTesting.review.menuCodeMonkeySent'));
      setTimeout(close, 1200);
    } catch (err: any) {
      setError(err.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  // Overrule the checker on this row: force it into the open issue queue (or lift the mark). Used when
  // an email was triaged as a safe skip but is actually a problem — nothing else about the row changes.
  const handleManualIssue = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await setAICheckerManualIssue(trace.id, !trace.manualIssue);
      setDone(t(trace.manualIssue ? 'adminTesting.review.unmarkIssueDone' : 'adminTesting.review.markIssueDone'));
      onChanged?.();
      setTimeout(close, 1200);
    } catch (err: any) {
      setError(err.message || 'Failed');
      setBusy(false);
    }
  };

  // Backprocess every invoice PDF in this email against the selected supplier — remembers the supplier
  // for the next row so a bundle of emails from one supplier needs no re-selecting.
  const handleBackprocess = () => {
    if (busy || !bpPlatform || !trace.gmailMessageId) return;
    lastBackprocessPlatform = bpPlatform;
    startRun('backprocess', bpPlatform);
  };

  const handleDelete = async () => {
    if (busy) return;
    if (!window.confirm(t(isRevenue ? 'adminTesting.review.deleteRevenueConfirm' : 'adminTesting.review.deleteOrderConfirm'))) return;
    setBusy(true); setError(null);
    try {
      if (isRevenue) {
        const r = await deleteAICheckerRevenue(trace.gmailMessageId as string);
        setDone(t('adminTesting.review.deleteRevenueDone', { deleted: r.deleted }));
      } else {
        const r = await deleteAICheckerOrder(trace.gmailMessageId as string);
        setDone(t('adminTesting.review.deleteOrderDone', { deleted: r.deleted, facturen: r.facturenDeleted }));
      }
      onChanged?.();
      setTimeout(close, 1200);
    } catch (err: any) {
      setError(err.message || 'Failed');
      setBusy(false);
    }
  };

  // Saving the list is instant; the re-audit it triggers is not, so that part runs on the row like every
  // other slow action and the menu closes as soon as the list is written.
  const handleSaveSafe = async () => {
    const type = view === 'subject' ? 'subject' : 'sender';
    const v = value.trim();
    if (!v || busy) return;
    setBusy(true); setError(null);
    try {
      const r = await updateAICheckerSafeSkip(type, 'add', v);
      onChanged?.();
      // Hand the re-audit to the row and keep going — no waiting on AI with the menu pinned open.
      if (canReaudit && !isRunning?.('reaudit')) onRunAction?.('reaudit');
      // The add also retro-marked matching older audit rows server-side — show how many before closing.
      if (r.retroCount) {
        setDone(t('adminTesting.review.safeSkipRetroDone', { count: r.retroCount }));
        setTimeout(close, 1600);
      } else {
        close();
      }
    } catch (err: any) {
      setError(err.message || 'Failed');
      setBusy(false);
    }
  };

  const itemCls = cn('w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-xs transition-colors disabled:opacity-50',
    'hover:bg-gray-100 dark:hover:bg-gray-700', primaryText);
  const inputCls = cn('w-full px-2 py-1.5 rounded-md border', 'border-gray-300 dark:border-gray-600', 'bg-white dark:bg-gray-700', primaryText, 'text-xs');

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        title={t('adminTesting.review.rowMenu')}
        className={cn('p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700', secondaryText, open && 'bg-gray-100 dark:bg-gray-700')}
      >
        <MoreVertical className="w-3.5 h-3.5" />
      </button>
      {open && pos && (
        <div
          ref={panelRef}
          style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, right: pos.right, zIndex: 50, maxHeight: maxH ?? undefined, overflowY: 'auto' }}
          className={cn('w-64 p-1.5 rounded-lg border shadow-lg', 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800')}
        >
          {view === 'menu' ? (
            <div className="space-y-0.5">
              {onOpenDetail && (
                <button onClick={() => { onOpenDetail(); close(); }} className={itemCls}>
                  <Eye className="w-3.5 h-3.5 shrink-0 text-gray-500 dark:text-gray-400" />
                  <span>{t('adminTesting.review.viewDetails')}</span>
                </button>
              )}
              <div className="flex items-center gap-2 px-3 py-2">
                <select
                  value={selectedMonkeyFlow}
                  onChange={(e) => setMonkeyFlow(e.target.value as 'wrong-processing' | 'email-issue')}
                  disabled={busy}
                  aria-label={t('adminTesting.review.menuCodeMonkey')}
                  className={cn('flex-1 min-w-0 px-1.5 py-1 rounded-md border text-xs', 'border-gray-300 dark:border-gray-600', 'bg-white dark:bg-gray-700', primaryText)}
                >
                  {canCheckWrongProcessing && (
                    <option value="wrong-processing">{t('adminTesting.review.menuCodeMonkeyWrongProcessing')}</option>
                  )}
                  <option value="email-issue">{t('adminTesting.review.menuCodeMonkeyGeneral')}</option>
                </select>
                <button
                  onClick={() => selectedMonkeyFlow === 'wrong-processing' ? startRun('monkeyWrongProcessing') : handleCodeMonkey()}
                  disabled={busy || (selectedMonkeyFlow === 'wrong-processing' && isRunning?.('monkeyWrongProcessing'))}
                  title={t('adminTesting.review.menuCodeMonkey')}
                  aria-label={t('adminTesting.review.menuCodeMonkey')}
                  className="shrink-0 p-1.5 rounded-md bg-teal-600 dark:bg-teal-600 text-white dark:text-white hover:bg-teal-700 dark:hover:bg-teal-700 disabled:opacity-40"
                >
                  <Terminal className="w-3.5 h-3.5" />
                </button>
              </div>
              <button onClick={() => openSafe('sender')} className={itemCls}>
                <ShieldCheck className="w-3.5 h-3.5 shrink-0 text-purple-600 dark:text-purple-400" />
                <span>{t('adminTesting.review.menuMarkSafeSender')}</span>
              </button>
              <button onClick={() => openSafe('subject')} className={itemCls}>
                <ShieldCheck className="w-3.5 h-3.5 shrink-0 text-purple-600 dark:text-purple-400" />
                <span>{t('adminTesting.review.menuMarkSafeSubject')}</span>
              </button>
              {/* Mirror of "mark safe": force this email into the open issue queue when the checker
                  called it a skip but it isn't. Toggles back off from the same slot. */}
              <button onClick={handleManualIssue} disabled={busy} title={t('adminTesting.review.menuMarkIssueHint')} className={itemCls}>
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-red-600 dark:text-red-400" />
                <span>{busy ? '…' : t(trace.manualIssue ? 'adminTesting.review.menuUnmarkIssue' : 'adminTesting.review.menuMarkIssue')}</span>
              </button>
              <button onClick={() => { setAddPlatformOpen(true); close(); }} className={itemCls}>
                <Plus className="w-3.5 h-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                <span>{t('adminTesting.addPlatform.menuItem')}</span>
              </button>
              {/* Everything below runs on the row: one click, menu closes, outcome lands as a chip. */}
              {canReaudit && (
                <button onClick={() => startRun('reaudit')} disabled={isRunning?.('reaudit')} className={itemCls}>
                  <Bug className="w-3.5 h-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
                  <span>{t('adminTesting.review.reaudit')}{isRunning?.('reaudit') ? ' …' : ''}</span>
                </button>
              )}
              {!!trace.rfc822MessageId && (
                <button onClick={() => startRun('deepAuto')} disabled={isRunning?.('deepAuto')} className={itemCls}>
                  <ListChecks className="w-3.5 h-3.5 shrink-0 text-sky-600 dark:text-sky-400" />
                  <span>{t('adminTesting.review.deepCheckMenu')}{isRunning?.('deepAuto') ? ' …' : ''}</span>
                </button>
              )}
              {!!trace.rfc822MessageId && (
                <button onClick={() => startRun('deepTotals')} disabled={isRunning?.('deepTotals')} className={itemCls}>
                  <ListChecks className="w-3.5 h-3.5 shrink-0 text-teal-600 dark:text-teal-400" />
                  <span>{t('adminTesting.review.deepCheckTotalsMenu')}{isRunning?.('deepTotals') ? ' …' : ''}</span>
                </button>
              )}
              {/* The delivery-note counterpart of "(totals)": this email's own raw rows against the
                  pakbon's printed totals. The factuur check is wrong for a pakbon in both directions —
                  it reads factuur-overridden amounts and expands to every order the covering factuur
                  bills, which on gnb turns one pakbon into a whole month. */}
              {!!trace.rfc822MessageId && (
                <button onClick={() => startRun('deepPakbon')} disabled={isRunning?.('deepPakbon')} title={t('adminTesting.review.deepCheckPakbonHint')} className={itemCls}>
                  <ListChecks className="w-3.5 h-3.5 shrink-0 text-cyan-600 dark:text-cyan-400" />
                  <span>{t('adminTesting.review.deepCheckPakbonMenu')}{isRunning?.('deepPakbon') ? ' …' : ''}</span>
                </button>
              )}
              {canDeepCheckAllFiles && (
                <button onClick={() => startRun('deepAllFiles')} disabled={isRunning?.('deepAllFiles')} className={itemCls}>
                  <ListChecks className="w-3.5 h-3.5 shrink-0 text-indigo-600 dark:text-indigo-400" />
                  <span>{t('adminTesting.review.deepCheckAllFilesMenu')}{isRunning?.('deepAllFiles') ? ' …' : ''}</span>
                </button>
              )}
              {!!trace.rfc822MessageId && (
                <button onClick={() => startRun('mixed')} disabled={isRunning?.('mixed')} className={itemCls}>
                  <ListChecks className="w-3.5 h-3.5 shrink-0 text-fuchsia-600 dark:text-fuchsia-400" />
                  <span>{t('adminTesting.mixedCheck.menuItem')}{isRunning?.('mixed') ? ' …' : ''}</span>
                </button>
              )}
              {/* Attach the source files this email's stored rows are missing — the manual escape hatch
                  for a backprocessed bundle the window run couldn't fully attribute. Only rows with no
                  file are touched, so an order keeps its own pakbon PDF. */}
              {!!trace.gmailMessageId && (
                <button onClick={() => startRun('attachFiles')} disabled={isRunning?.('attachFiles')} className={itemCls}>
                  <Layers className="w-3.5 h-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  <span>{t('adminTesting.review.missingFilesRowMenu')}{isRunning?.('attachFiles') ? ' …' : ''}</span>
                </button>
              )}
              {canBackprocess && (
                <div className="flex items-center gap-2 px-3 py-2">
                  <Layers className="w-3.5 h-3.5 shrink-0 text-indigo-600 dark:text-indigo-400" />
                  <select
                    value={bpPlatform}
                    onChange={(e) => setBpPlatform(e.target.value)}
                    disabled={busy}
                    className={cn('flex-1 min-w-0 px-1.5 py-1 rounded-md border text-xs', 'border-gray-300 dark:border-gray-600', 'bg-white dark:bg-gray-700', primaryText)}
                  >
                    <option value="">{t('adminTesting.backprocess.platformPlaceholder')}</option>
                    {bpPlatforms.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <button
                    onClick={handleBackprocess}
                    disabled={busy || isRunning?.('backprocess') || !bpPlatform}
                    className={cn('px-2 py-1 rounded-md text-xs font-medium bg-indigo-600 dark:bg-indigo-600 text-white dark:text-white hover:bg-indigo-700 dark:hover:bg-indigo-700 disabled:opacity-40', 'shrink-0')}
                  >
                    {isRunning?.('backprocess') ? '…' : t('adminTesting.backprocess.run')}
                  </button>
                </div>
              )}
              {canAddMissingFactuur && (
                <button onClick={() => startRun('addFactuur')} disabled={isRunning?.('addFactuur')} className={itemCls}>
                  <RotateCw className="w-3.5 h-3.5 shrink-0 text-rose-600 dark:text-rose-400" />
                  <span>{t('adminTesting.review.addMissingFactuur')}{isRunning?.('addFactuur') ? ' …' : ''}</span>
                </button>
              )}
              {canDelete && (
                <button onClick={handleDelete} disabled={busy} className={itemCls}>
                  <Trash2 className="w-3.5 h-3.5 shrink-0 text-red-600 dark:text-red-400" />
                  <span>{busy ? '…' : t(isRevenue ? 'adminTesting.review.deleteRevenue' : 'adminTesting.review.deleteOrder')}</span>
                </button>
              )}
              {done && <p className="px-3 py-1 text-[10px] text-green-600 dark:text-green-400">{done}</p>}
              {error && <p className="px-3 py-1 text-[10px] text-red-500 dark:text-textRed-dark">{error}</p>}
            </div>
          ) : (
            <div className="p-1.5 space-y-2">
              <p className={cn(primaryText, semiBoldText, 'text-xs')}>
                {view === 'sender' ? t('adminTesting.review.menuSafeSenderTitle') : t('adminTesting.review.menuSafeSubjectTitle')}
              </p>
              <input
                autoFocus
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveSafe(); }}
                placeholder={view === 'sender' ? t('adminTesting.review.safeSkipSenderPlaceholder') : t('adminTesting.review.safeSkipSubjectPlaceholder')}
                className={inputCls}
              />
              <p className={cn(secondaryText, 'text-[10px]')}>
                {canReaudit ? t('adminTesting.review.menuSafeHint') : t('adminTesting.review.menuSafeHintNoReaudit')}
              </p>
              {error && <p className="text-[10px] text-red-500 dark:text-textRed-dark">{error}</p>}
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => { setView('menu'); setError(null); }} className={cn('px-2 py-1 rounded-md text-xs', secondaryText, 'hover:bg-gray-100 dark:hover:bg-gray-700')}>
                  {t('adminTesting.review.menuBack')}
                </button>
                <button
                  onClick={handleSaveSafe}
                  disabled={busy || !value.trim()}
                  className={cn('px-3 py-1 rounded-md bg-purple-600 dark:bg-purple-600 text-white dark:text-white text-xs hover:bg-purple-700 dark:hover:bg-purple-700 disabled:opacity-40', semiBoldText)}
                >
                  {busy ? '…' : (canReaudit ? t('adminTesting.review.menuSave') : t('adminTesting.review.menuSaveOnly'))}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {addPlatformOpen && (
        <AddRestaurantPlatformModal
          initialPlatform={trace.platform}
          initialOrderEmail={extractSenderAddress(trace.emailFrom)}
          onClose={() => setAddPlatformOpen(false)}
        />
      )}
    </>
  );
}
